import * as React from "react";
import {
  IValidateAccessHOCProps,
  IValidateAccessState,
} from "@market/components/ValidateAccessHOC/IValidateAccessHOC";
import Loader from "@appComponents/Loader/Loader";
import RestrictedView from "@market/components/RestrictedView/RestrictedView";
import SMAccessValidationTypes from "@market/enums/SMAccessValidationTypes";
import { TUserInfo } from "@features/reducer.info";
import { ETiers } from "@appComponents/components/IdentityStatusLabel/IIdentityStatusLabel";
import { hasRequiredTier } from "@features/utils/functions";

export default function validateAccessHOC<OriginalProps>(
  WrappedComponent: React.ComponentClass<OriginalProps>
): React.ComponentClass<OriginalProps & IValidateAccessHOCProps & any> {
  let isBuyAction: boolean;
  let isSellAction: boolean;
  let isOrderPage: boolean;

  return class validateAccess extends React.Component<
    OriginalProps & IValidateAccessHOCProps,
    IValidateAccessState
  > {
    state: IValidateAccessState = {
      loader: true,
      isChecked: false,
      restrictedViewReason: null,
      pitchClosedTime: undefined,
    };

    shouldComponentUpdate(nextProps: any, nextState: any) {
      if (
        nextProps.userInfo &&
        nextProps.spv &&
        nextProps.spv.detailedCompanyAsset
      ) {
        return true;
      }
      return false;
    }

    componentDidMount() {
      const URL = this.props.match.url;
      isBuyAction = URL.indexOf("/buy") !== -1;
      isSellAction = URL.indexOf("/sell") !== -1;
      isOrderPage = URL.indexOf("/orders") !== -1;

      if (this.props.userInfo !== null && this.props.spv) {
        this.setState({
          restrictedViewReason: this.verifyAccess(this.props.userInfo),
          loader: false,
        });
      }
    }

    componentDidUpdate(prevProps: OriginalProps & IValidateAccessHOCProps) {
      if (
        this.props.userInfo !== null &&
        this.props.spv &&
        !this.state.isChecked &&
        !this.state.restrictedViewReason
      ) {
        this.setState({
          restrictedViewReason: this.verifyAccess(this.props.userInfo),
          loader: false,
          isChecked: true,
        });
      }
    }

    isAmlFailed() {
      const {
        userInfo: { verification_status, is_verified },
      } = this.props;

      return (
        !is_verified &&
        verification_status &&
        verification_status.includes(SMAccessValidationTypes.aml_failed)
      );
    }

    isNotCertified(): boolean {
      const { userInfo } = this.props;

      return (
        !userInfo.is_certified_or_selfcertified ||
        userInfo.certification_status === "rejected_certification" ||
        (isSellAction &&
          !userInfo.us_resident &&
          userInfo.certification_status === "self_certified")
      );
    }

    isNotVerified = (): boolean => {
      const { userInfo } = this.props;

      return !hasRequiredTier(ETiers.tier_0_kyc, userInfo.tier);
    };

    noSellableShares = (): boolean => {
      return !!(
        isOrderPage &&
        isSellAction &&
        this.props.spv.orderShares &&
        this.props.spv.orderShares.assets &&
        !this.props.spv.orderShares.assets.find(
          (el) => el.sellable_number_of_shares > 0
        )
      );
    };

    verifyAccess = (data: TUserInfo) => {
      if (data.restricted_investing_cayman)
        return SMAccessValidationTypes.restricted_investing_cayman;

      if (isSellAction && data.sell_restricted_by_admin)
        return SMAccessValidationTypes.sell_restricted_by_admin;
      if (isBuyAction && data.buy_restricted_by_admin)
        return SMAccessValidationTypes.buy_restricted_by_admin;

      if (isSellAction && data.sell_restricted_by_pep_sanction)
        return SMAccessValidationTypes.sell_restricted_by_pep_sanction;
      if (isBuyAction && data.buy_restricted_by_pep_sanction)
        return SMAccessValidationTypes.buy_restricted_by_pep_sanction;

      if (isSellAction && data.sell_restricted_by_user_statement)
        return SMAccessValidationTypes.sell_restricted_by_user_statement;
      if (isBuyAction && data.buy_restricted_by_user_statement)
        return SMAccessValidationTypes.buy_restricted_by_user_statement;

      if (isSellAction && data.sell_restricted_by_risk_rating)
        return SMAccessValidationTypes.sell_restricted_by_risk_rating;
      if (isBuyAction && data.buy_restricted_by_risk_rating)
        return SMAccessValidationTypes.buy_restricted_by_risk_rating;

      if (isSellAction && data.sell_restricted_by_tier)
        return SMAccessValidationTypes.sell_restricted_by_tier;
      if (isBuyAction && data.buy_restricted_by_tier)
        return SMAccessValidationTypes.buy_restricted_by_tier;

      if (isSellAction && data.sell_restricted_by_country_uk)
        return SMAccessValidationTypes.sell_restricted_by_country_uk;
      if (isBuyAction && data.buy_restricted_by_country_uk)
        return SMAccessValidationTypes.buy_restricted_by_country_uk;

      if (isSellAction && data.sell_restricted_by_2fa)
        return SMAccessValidationTypes.sell_restricted_by_2fa;
      if (isBuyAction && data.buy_restricted_by_2fa)
        return SMAccessValidationTypes.buy_restricted_by_2fa;
      if (this.isAmlFailed()) return SMAccessValidationTypes.aml_failed;
      if (this.isNotCertified()) return SMAccessValidationTypes.not_certified;
      if (this.isNotVerified()) return SMAccessValidationTypes.not_verified;
      if (!data._2fa_enabled) return SMAccessValidationTypes.has_no_2fa_enabled;
      if (isBuyAction && data.us_resident)
        return SMAccessValidationTypes.is_us_resident;

      if (
        isOrderPage &&
        isSellAction &&
        this.props.spv!.orderShares!.has_active_sell_orders
      ) {
        return SMAccessValidationTypes.has_active_sell_order;
      }

      if (this.noSellableShares())
        return SMAccessValidationTypes.no_shares_for_sell;

      if (
        isOrderPage &&
        isBuyAction &&
        this.props.spv!.orderShares!.has_active_buy_orders
      ) {
        return SMAccessValidationTypes.has_active_buy_order;
      }

      return null;
    };

    render() {
      if (this.state.loader) return <Loader moreSpace={true} />;

      if (this.state.restrictedViewReason) {
        return (
          <RestrictedView
            reason={this.state.restrictedViewReason}
            investor_slug={this.props.userInfo.nickname}
          />
        );
      }

      return <WrappedComponent {...this.props} />;
    }
  };
}
